import React, { FunctionComponent, useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import cuid from 'cuid';
import useConfig from '@empiriecom/module-inspire-components/components/hooks/useConfig';
import Footer from '@empiriecom/module-inspire-components/components/Footer';
import { FontSizes } from '@empiriecom/module-inspire-components/utils/style.drupal';
import { Languages } from '@empiriecom/module-inspire-components/interfaces/types';
import { FragmentConfig } from '@/config/types';
import userPreferredLocale from '@empiriecom/module-inspire-components/utils/userPreferredLocale';
import { getCmsApiUrl } from '@empiriecom/module-inspire-components/utils/apiUrl';
import { getStaticPageApiUrl } from '@empiriecom/module-inspire-components/utils/apiUrl.staticPage';
import { trackingProvider } from '@empiriecom/module-inspire-components/providers/trackingProvider';
import { DataGlobal } from '@empiriecom/module-inspire-components/interfaces/cmsdata';
import { parse } from 'query-string';
import { useLocation } from 'react-router';
import * as request from '@empiriecom/module-inspire-components/utils/useFetch';

const StyledPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center; /* horizontal alignment */
  align-items: center; /* vertical alignment */
  text-align: left;
  ${FontSizes()}
`;

const GlobalStyle = createGlobalStyle`
  .page-container .distance-variation-1,
  .page-container .distance-fullwidth {
    @media only screen and (min-width: ${({ desktopMinWidth }: { desktopMinWidth: string }) =>
      desktopMinWidth}) {
      padding-left: 0;
      padding-right: 0;
    }
  }
  .page-container .distance-variation-1 {
    padding: 0 1.5rem;
  }

  .page-container .distance-fullwidth {
    max-width: unset;
  }
`;

const PageContainer: FunctionComponent<{
  history?: any;
  testData?: any;
  configuration?: { useFallback?: boolean; fetchTimeout?: number };
  onlySocket: boolean;
}> = ({ testData, configuration, onlySocket }): JSX.Element => {
  const config = useConfig<FragmentConfig>();
  const location = useLocation();
  const { revisionId = '' } = parse(location.search) || {
    revisionId: '',
  };

  const preferredLocale = userPreferredLocale(
    location?.pathname || '',
    config?.apiParams?.values?.apiDefaultParams?.locale,
    config?.apiParams?.values?.apiDefaultParams?.localesToSwitch,
  );

  const language = preferredLocale.substring(0, 2) as Languages;

  const apiCmsUrl = getCmsApiUrl(
    language,
    config?.apiCmsUrl?.values?.endPoint || '', // pathname,
    revisionId as string,
    config?.apiCmsUrl?.values?.url,
    config?.replace?.values?.page,
  );

  const apiCmsUrlStaticFileCms =
    getStaticPageApiUrl({
      language,
      pathname: config?.apiCmsUrl?.values?.endPoint || '',
      url: config?.apiCmsUrlStaticPages?.values?.url || '',
    }) || '';

  const defaultData = testData || { field_pagegroup: 'missing' };
  const fetchTimerPrimary = config?.fetchParams?.values?.timeout || 5000;
  const fetchTimerSecondary =
    configuration?.fetchTimeout || config?.fetchParams?.values?.timeoutFallback || 5000;

  const [cmsData, setCmsData] = useState<DataGlobal>(defaultData);

  useEffect(() => {
    request
      .useFetch({
        urlPrimary: apiCmsUrlStaticFileCms,
        urlSecondary: apiCmsUrl,
        fetchTimerPrimary,
        fetchTimerSecondary,
      })
      .then((data: any) => {
        setCmsData(data);
      });
  }, []);

  const itemID = cuid();

  const ElementPageContent = (): JSX.Element =>
    cmsData && cmsData?.paragraph ? (
      <Footer footerData={cmsData.paragraph} key={itemID} onlySocket={!!onlySocket} />
    ) : (
      <></>
    );

  if (cmsData?.field_page_landmark) {
    trackingProvider.setLandmark({
      landmarkName: 'emma',
      template: cmsData.field_page_landmark,
    });
  }

  if (cmsData) {
    return (
      <>
        <GlobalStyle desktopMinWidth={config?.mediaBreakpoints?.values?.desktopMinWidth || ''} />
        <StyledPageContainer className="page-container" data-testid="page-container">
          <ElementPageContent />
        </StyledPageContainer>
      </>
    );
  }
  return <></>;
};

export default PageContainer;
